// Read-only projection of backend 63696b5. No API, preview assets or local rewards.
const kinds = new Map([['NEWBIE_TASK', 'newcomer'], ['VOTE', 'vote'], ['FIRST_DEPOSIT', 'deposit']]);
const types = new Map([...kinds].map(([type, kind]) => [kind, type]));
const int64Max = 9223372036854775807n;
const safeCentsMax = BigInt(Number.MAX_SAFE_INTEGER);
const taskTypes = new Set(['REGISTER', 'BIND_PHONE', 'BIND_BANK_CARD', 'PROFILE', 'FIRST_DEPOSIT', 'TOTAL_DEPOSIT']);
const own = (object, field) => Object.hasOwn(object, field) ? object[field] : undefined;

export function campaignKind(activityType) { return kinds.get(activityType) || ''; }
export function campaignType(kind) { return types.get(kind) || ''; }

function fail(field, detail) { throw new Error(`活动数据适配失败：${field} ${detail}`); }
function object(value, field) {
  if (!value || typeof value !== 'object' || Array.isArray(value) ||
    ![Object.prototype, null].includes(Object.getPrototypeOf(value))) fail(field, '必须是对象');
  return value;
}
function list(value, field) {
  if (!Array.isArray(value)) fail(field, '必须是数组');
  return value;
}
function text(value, field, required = false) {
  if (value == null && !required) return '';
  if (typeof value !== 'string' || (required && !value.trim())) fail(field, '必须是有效文本');
  return value;
}
function boolean(value, field) {
  if (value == null) return null;
  if (typeof value !== 'boolean') fail(field, '必须是布尔值或未知');
  return value;
}
function id(value, field) {
  if (typeof value === 'number' && Number.isSafeInteger(value) && value > 0) return String(value);
  if (typeof value === 'string' && /^[1-9]\d{0,18}$/.test(value) && BigInt(value) <= int64Max) return value;
  fail(field, '必须是正 int64 编号，数值编号不得丢失精度');
}
function key(value, field) {
  if (typeof value !== 'string' || !/^[A-Za-z0-9_-]{1,40}$/.test(value)) fail(field, '稳定编码无效');
  return value;
}
function integer(value, field, { optional = true, signed = false } = {}) {
  if (value == null && optional) return null;
  if (typeof value !== 'number' || !Number.isSafeInteger(value) || (!signed && value < 0)) fail(field, '必须是安全整数');
  return value;
}
function count(value, field) {
  if (value == null) return null;
  if (typeof value === 'number') return integer(value, field);
  // Preserve exact int64 strings, including values above Number.MAX_SAFE_INTEGER.
  if (typeof value === 'string' && /^(0|[1-9]\d{0,18})$/.test(value) && BigInt(value) <= int64Max) return value;
  fail(field, '必须是非负 int64 票数');
}
function decimal(value, field, { required = false, twoPlaces = false, amount = false } = {}) {
  if (value == null && !required) return null;
  if (value === '' && !required) return '';
  const pattern = twoPlaces ? /^(0|[1-9]\d*)\.\d{2}$/ :
    amount ? /^(0|[1-9]\d*)(?:\.\d{1,2})?$/ : /^(0|[1-9]\d*)(?:\.\d+)?$/;
  if (typeof value !== 'string' || value.length > 128 || !pattern.test(value)) {
    fail(field, twoPlaces ? '必须是非负两位小数金额字符串' : '必须是非负十进制字符串');
  }
  return value;
}
function cents(value, field) {
  const source = decimal(value, field, { twoPlaces: true });
  if (source == null || source === '') return null;
  const result = BigInt(source.replace('.', ''));
  if (result > safeCentsMax) fail(field, '金额分值超过安全整数范围');
  return Number(result);
}

// Images are optional: unsafe/missing sources render without an image. A wrong
// JSON type is a malformed response. Never manufacture a dark or demo fallback.
function imageUrl(value, field) {
  const source = text(value, field);
  if (!source) return '';
  if (/[\s\u0000-\u001f\u007f\\<>"'`(){}]/.test(source) ||
    !(/^(?:https?:\/\/[^/?#]|\/(?!\/))/i.test(source))) return '';
  try {
    const url = new URL(source, 'https://campaign-image.invalid');
    if (!['http:', 'https:'].includes(url.protocol) || !url.hostname || url.username || url.password) return '';
    let decoded = source;
    for (let i = 0; i < 5 && decoded.includes('%'); i++) {
      const next = decodeURIComponent(decoded);
      if (next === decoded) break;
      decoded = next;
    }
    if (/%[0-9a-f]{2}|[\u0000-\u001f\u007f\\<>"'`(){}]/i.test(decoded) ||
      /^\/\//.test(decoded) || /(?:^|\/)activity-demo-/i.test(decoded)) return '';
    return source;
  } catch (_) { return ''; }
}
function uniqueRows(value, field, parseKey = key) {
  const seen = new Set();
  return Array.from(list(value, field), (row, index) => {
    const path = `${field}[${index}]`;
    object(row, path);
    const rowKey = parseKey(own(row, parseKey === id ? 'id' : 'key'), `${path}.${parseKey === id ? 'id' : 'key'}`);
    const uniqueKey = rowKey.toLowerCase();
    if (seen.has(uniqueKey)) fail(field, `存在重复编码 ${rowKey}`);
    seen.add(uniqueKey);
    return { row, path, key: rowKey, index };
  });
}
function progressMap(state) {
  const entries = uniqueRows(own(state, 'progress'), 'state.progress');
  return new Map(entries.map(({ row, path, key: rowKey }) => [rowKey, {
    completed: boolean(own(row, 'completed'), `${path}.completed`),
    claimed: boolean(own(row, 'claimed'), `${path}.claimed`),
    currentAmount: decimal(own(row, 'currentAmount'), `${path}.currentAmount`, { amount: true }),
    count: count(own(row, 'count'), `${path}.count`)
  }]));
}
function configurationOrder(left, right) {
  return (left.sortNo ?? Infinity) - (right.sortNo ?? Infinity) || left.index - right.index;
}
function configuredItems(rules, name) {
  return uniqueRows(own(rules, name), `state.rules.${name}`).map(entry => ({
    ...entry, sortNo: integer(own(entry.row, 'sortNo'), `${entry.path}.sortNo`, { signed: true })
  })).sort(configurationOrder);
}

// Manual requests reserve one entitlement; only a completed receipt means money arrived.
function reviewFields(row, path, claimed, eligible) {
  const manualReview = boolean(own(row, 'manualReview'), `${path}.manualReview`) === true;
  const venueReward = boolean(own(row, 'venueReward'), `${path}.venueReward`) === true;
  const venueName = text(own(row, 'venueName'), `${path}.venueName`);
  const payoutStatus = text(own(row, 'payoutStatus'), `${path}.payoutStatus`);
  if (payoutStatus && (!venueReward || !['READY','PENDING','SUCCESS','FAILED','CANCELLED'].includes(payoutStatus) || eligible !== false || typeof claimed !== 'boolean' || !venueName || claimed !== (payoutStatus === 'SUCCESS'))) fail(path, '场馆原奖励状态无效');
  if (claimed && venueReward && payoutStatus !== 'SUCCESS') fail(path, '场馆奖励缺少实际成功凭证');
  const reviewStatus = text(own(row, 'reviewStatus'), `${path}.reviewStatus`);
  const reviewRemark = text(own(row, 'reviewRemark'), `${path}.reviewRemark`);
  if (reviewStatus && (!manualReview || !['PENDING','APPROVED','REJECTED'].includes(reviewStatus) || eligible !== false || typeof claimed !== 'boolean' || claimed && reviewStatus !== 'APPROVED')) fail(path, '人工审核状态无效');
  if (manualReview && claimed && reviewStatus !== 'APPROVED') fail(path, '已领取人工礼包缺少通过凭证');
  return { manualReview, reviewStatus, reviewRemark, venueReward, venueName, payoutStatus };
}
function validateOriginalReview(row, path) {
  if (!row.reviewStatus && !row.payoutStatus) return;
  if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(row.requestId) || !row.recordId || !row.claimed && row.completedAt) fail(path, '人工申请原凭证不完整');
}

function newcomer(state, rules, progress) {
  const tasks = configuredItems(rules, 'tasks').map(({ row, path, key: taskKey, sortNo }) => {
    const type = text(own(row, 'type'), `${path}.type`, true);
    if (!taskTypes.has(type)) fail(`${path}.type`, '任务类型无效');
    const status = progress.get(taskKey);
    return {
      id: taskKey, key: taskKey, type, sortNo,
      title: text(own(row, 'title'), `${path}.title`, true),
      imageUrl: imageUrl(own(row, 'imageUrl'), `${path}.imageUrl`),
      targetAmount: decimal(own(row, 'targetAmount'), `${path}.targetAmount`, { amount: true }),
      rewardAmount: decimal(own(row, 'rewardAmount'), `${path}.rewardAmount`, { amount: true }),
      completed: status?.completed ?? null, claimed: status?.claimed ?? null,
      currentAmount: status?.currentAmount ?? null
    };
  });
  const rawPackage = own(state, 'newbiePackage');
  let bundle = null;
  if (rawPackage != null) {
    object(rawPackage, 'state.newbiePackage');
    const enabled = boolean(own(rawPackage, 'enabled'), 'state.newbiePackage.enabled');
    if (enabled === true) {
      const claimed = boolean(own(rawPackage, 'claimed'), 'state.newbiePackage.claimed');
      const eligible = boolean(own(rawPackage, 'eligible'), 'state.newbiePackage.eligible');
      const rewardAmount = decimal(own(rawPackage, 'rewardAmount'), 'state.newbiePackage.rewardAmount', { required: true, twoPlaces: true });
      if (!/[1-9]/.test(rewardAmount)) fail('state.newbiePackage.rewardAmount', '整体奖励必须大于0');
      const currency = text(own(rawPackage, 'currency'), 'state.newbiePackage.currency', true);
      if (currency !== 'CNY') fail('state.newbiePackage.currency', '当前礼包只支持CNY');
      const rewardMaximum = decimal(own(rawPackage, 'rewardMaximum'), 'state.newbiePackage.rewardMaximum', { twoPlaces: true });
      if (rewardMaximum && (claimed || cents(rewardMaximum, 'state.newbiePackage.rewardMaximum') < cents(rewardAmount, 'state.newbiePackage.rewardAmount'))) fail('state.newbiePackage.rewardMaximum', '随机范围无效或已领取仍返回预估范围');
      const review = reviewFields(rawPackage, 'state.newbiePackage', claimed, eligible);
      bundle = { enabled, claimed, eligible, rewardAmount, rewardMaximum, currency, ...review,
        recordId: claimed === true || review.reviewStatus || review.payoutStatus ? id(own(rawPackage, 'recordId'), 'state.newbiePackage.recordId') : '',
        requestId: text(own(rawPackage, 'requestId'), 'state.newbiePackage.requestId'),
        reasonCode: text(own(rawPackage, 'reasonCode'), 'state.newbiePackage.reasonCode'),
        reason: text(own(rawPackage, 'reason'), 'state.newbiePackage.reason'),
        registeredAt: integer(own(rawPackage, 'registeredAt'), 'state.newbiePackage.registeredAt'),
        expiresAt: integer(own(rawPackage, 'expiresAt'), 'state.newbiePackage.expiresAt'),
        completedAt: integer(own(rawPackage, 'completedAt'), 'state.newbiePackage.completedAt') };
      validateOriginalReview(bundle, 'state.newbiePackage');
      if (claimed === true && (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(bundle.requestId) || !bundle.completedAt)) fail('state.newbiePackage', '原整体领取凭证不完整');
      if (claimed === true && eligible === true) fail('state.newbiePackage', '已领取礼包不能再次可领');
    }
  }
  const giftProgress = new Map(uniqueRows(own(state, 'newbieGifts') ?? [], 'state.newbieGifts').map(({row,key:giftKey}) => [giftKey,row]));
  const gifts = configuredItems({newbieGifts: own(rules,'newbieGifts') ?? []}, 'newbieGifts').map(({row,path,key:giftKey,sortNo}) => {
    const level = /^newbie-first-game-gift-level-([1-9]|1[0-9]|20)$/.exec(giftKey);
    const firstGame = giftKey === 'newbie-first-game-gift' || Boolean(level);
    if (!['newbie-deposit-gift','newbie-withdrawal-gift','newbie-first-game-gift','newbie-game-gift','newbie-revival-gift'].includes(giftKey) && !level) fail(path, '礼包类型无效');
    if (giftKey === 'newbie-deposit-gift' && (![1,2].includes(own(row,'depositType')) || ![1,2].includes(own(row,'taskCycle')))) fail(path,'存款任务周期或类型无效');
    if ((giftKey === 'newbie-withdrawal-gift' || firstGame) && ![1,2].includes(own(row,'taskCycle'))) fail(path,'体验任务周期无效');
    if (firstGame && (level ? own(row,'taskForm') !== 2 || own(row,'betType') !== 2 || own(row,'gameLevel') !== Number(level[1]) : own(row,'taskForm') !== 1 || ![1,2].includes(own(row,'betType')))) fail(path,'游戏体验任务或投注方式无效');
    const fact = giftProgress.get(giftKey);
    if (!fact) fail(path, '缺少服务端礼包进度');
    const eligible = boolean(own(fact,'eligible'), `${path}.eligible`), claimed = boolean(own(fact,'claimed'), `${path}.claimed`);
    if (eligible === null || claimed === null || (claimed && eligible)) fail(path,'领取状态无效');
    const review = reviewFields(fact, path, claimed, eligible);
    const requestId = text(own(fact,'requestId'), `${path}.requestId`);
    const recordId = claimed || review.reviewStatus || review.payoutStatus ? id(own(fact,'recordId'), `${path}.recordId`) : '';
    const completedAt = integer(own(fact,'completedAt'), `${path}.completedAt`);
    validateOriginalReview({ ...review, claimed, recordId, requestId, completedAt }, path);
    const taskCycle = integer(own(row,'taskCycle'), `${path}.taskCycle`);
    const periodKey = text(own(fact,'periodKey'), `${path}.periodKey`);
    if (taskCycle === 2 && (!/^\d{4}-\d{2}-\d{2}$/.test(periodKey) || !Number.isFinite(Date.parse(periodKey+'T00:00:00+08:00')) || new Date(Date.parse(periodKey+'T00:00:00+08:00')+8*3600*1000).toISOString().slice(0,10)!==periodKey || (claimed && new Date((completedAt + 8*3600)*1000).toISOString().slice(0,10) !== periodKey))) fail(path,'每日礼包原日期无效');
    if (taskCycle !== 2 && periodKey) fail(path,'一次礼包不能携带每日日期');
    if (claimed && (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(requestId) || !completedAt)) fail(path, '原礼包凭证不完整');
    return {
      key:giftKey, title:text(own(fact,'title'), `${path}.title`,true), sortNo,
      imageUrl:imageUrl(own(row,'webImageUrl'),`${path}.webImageUrl`),
      eligible, claimed, recordId, requestId, completedAt, taskCycle, periodKey, ...review,
      turnoverMultiplier:decimal(own(row,'turnoverMultiplier'),`${path}.turnoverMultiplier`,{required:true}),
      gameLevel:integer(own(row,'gameLevel'),`${path}.gameLevel`),
      taskForm:integer(own(row,'taskForm'),`${path}.taskForm`),
      betType:integer(own(row,'betType'),`${path}.betType`),
      depositType:integer(own(row,'depositType'),`${path}.depositType`),
      currentAmount:decimal(own(fact,'currentAmount'),`${path}.currentAmount`,{required:firstGame || ['newbie-deposit-gift','newbie-withdrawal-gift'].includes(giftKey)}),
      successDays:integer(own(fact,'successDays'),`${path}.successDays`,{optional:false}),
      requiredDays:integer(own(fact,'requiredDays'),`${path}.requiredDays`,{optional:false}),
      netLoss:decimal(own(fact,'netLoss'),`${path}.netLoss`,{required:true}),
      rewardAmount:decimal(own(fact,'rewardAmount'),`${path}.rewardAmount`,{required:true,twoPlaces:true}),
      reason:text(own(fact,'reason'),`${path}.reason`),
      startsAt:integer(own(fact,'startsAt'),`${path}.startsAt`),
      endsAt:integer(own(fact,'endsAt'),`${path}.endsAt`),
      expiresAt:integer(own(fact,'expiresAt'),`${path}.expiresAt`),
      periodDays:integer(own(row,'periodDays'),`${path}.periodDays`),
      requirement:integer(own(row,'claimRequirement'),`${path}.claimRequirement`),
      depositAmount:decimal(own(row,'depositAmount'),`${path}.depositAmount`),
      validBetAmount:decimal(own(row,'validBetAmount'),`${path}.validBetAmount`),
      deficitAmount:decimal(own(row,'deficitAmount'),`${path}.deficitAmount`),
      deficitScale:decimal(own(row,'deficitScale'),`${path}.deficitScale`),
      rewardLimit:decimal(own(row,'rewardLimit'),`${path}.rewardLimit`)
    };
  });
  if (giftProgress.size !== gifts.length) fail('state.newbieGifts','礼包配置与进度不一致');
  const registeredAt = bundle?.registeredAt;
  const registrationDate = registeredAt > 0 && registeredAt < 253402185600
    ? new Date((registeredAt + 8 * 3600) * 1000).toISOString().slice(0, 19).replace('T', ' ')
    : text(own(state, 'registrationDate'), 'state.registrationDate');
  return {
    tasks, gifts, registrationDate,
    newbieDays: integer(own(rules, 'newbieDays'), 'state.rules.newbieDays'),
    eligible: bundle ? bundle.eligible === true || bundle.reasonCode === 'TASKS_INCOMPLETE' || bundle.claimed === true || Boolean(bundle.reviewStatus || bundle.payoutStatus) : boolean(own(state, 'eligible'), 'state.eligible'),
    claimed: bundle ? bundle.claimed : boolean(own(state, 'claimed'), 'state.claimed'),
    package: bundle,
    reward: bundle?.rewardMaximum ? `${bundle.rewardAmount}～${bundle.rewardMaximum}` : bundle?.rewardAmount ?? null,
    readOnlyTaskNavigation: true,
    claimAvailable: bundle?.enabled === true,
    canClaim: (bundle?.eligible === true || bundle?.venueReward === true && bundle?.payoutStatus === 'READY') && bundle.claimed === false && tasks.length > 0 && tasks.every(task => task.completed === true),
    capabilityNotice: bundle ? bundle.claimed === true ? '' : bundle.reason : gifts.length ? '' : '统一礼包领取暂未开放'
  };
}
function vote(state, rules, progress) {
  const candidates = configuredItems(rules, 'candidates').map(({ row, path, key: candidateKey, sortNo }, index) => ({
    id: candidateKey, key: candidateKey, sortNo, index,
    name: text(own(row, 'title'), `${path}.title`, true),
    logo: imageUrl(own(row, 'imageUrl'), `${path}.imageUrl`),
    votes: progress.get(candidateKey)?.count ?? null
  }));
  const completeCounts = candidates.every(candidate => candidate.votes !== null);
  if (completeCounts) candidates.sort((left, right) => {
    const a = BigInt(left.votes), b = BigInt(right.votes);
    return a === b ? left.index - right.index : a > b ? -1 : 1;
  });
  return {
    candidates: candidates.map(({ index, ...candidate }, position) => ({ ...candidate, rank: completeCounts ? position + 1 : null })),
    // 63696b5 has one activity-wide vote receipt, with no stage or ballot budget.
    stages: [], stageId: '', availableVotes: '—', canVote: false,
    eligible: boolean(own(state, 'eligible'), 'state.eligible'),
    rankIsDisplayOrder: true,
    rankingNotice: completeCounts ? '按票数降序展示，平票按配置顺序排列；序号仅为展示排序' : '部分票数未提供，暂不展示排序',
    capabilityNotice: '多阶段投票暂未开放'
  };
}
function deposit(state) {
  const centerClaimSupported = boolean(own(state, 'centerClaimSupported'), 'state.centerClaimSupported') === true;
  if (centerClaimSupported && state.applicationSupported === true) fail('state.centerClaimSupported', '不能同时提供本金划转');
  if (state.applicationSupported === true && !/^[0-9a-f]{64}$/.test(state.rulesVersion || '')) fail('state.rulesVersion', '规则版本无效');
  const requirement = state.requirement === undefined ? 2 : state.requirement;
  if (![0, 1, 2].includes(requirement)) fail('state.requirement', '首存次数口径无效');
  const venueRequired = state.venueRequired === undefined ? false : boolean(state.venueRequired, 'state.venueRequired');
  if (venueRequired !== (centerClaimSupported && requirement === 1)) fail('state.venueRequired', '场馆核验口径不一致');
  const tiers = Array.from(list(own(state, 'rewardTierList'), 'state.rewardTierList'), (row, index) => {
    const path = `state.rewardTierList[${index}]`;
    object(row, path);
    const rewardMode = text(own(row, 'rewardMode'), `${path}.rewardMode`);
    if (rewardMode && !['FIXED', 'PERCENT'].includes(rewardMode)) fail(`${path}.rewardMode`, '奖励方式无效');
    const tier = { key: `config-row-${index}`, displayOnly: true, rewardMode };
    if (state.applicationSupported === true) tier.planId = own(row,'planId');
    if (state.applicationSupported === true && !/^fdp1_[0-9a-f]{64}$/.test(tier.planId)) fail(`${path}.planId`, '服务端档位编号无效');
    for (const field of ['requiredAmount', 'bonusRate', 'fixedBonusAmount', 'maxBonusAmount', 'turnoverMultiplier']) {
      tier[field] = decimal(own(row, field), `${path}.${field}`, { required: field === 'requiredAmount' });
    }
    // common/activity/depositReward.go CalculateDepositReward: deposit * rate / 100.
    // Keep the percent as an exact string. It is never a client settlement input.
    tier.displayPercent = rewardMode === 'PERCENT' && tier.bonusRate ? tier.bonusRate : null;
    tier.tableLabel = tier.displayPercent !== null ? `${tier.displayPercent}%` :
      rewardMode === 'FIXED' && tier.fixedBonusAmount ? `固定奖励 ${tier.fixedBonusAmount} 元` : '奖励方式待确认';
    return tier;
  });
  const seen = new Set();
  const venueIdList = Array.from(list(own(state, 'venueIdList'), 'state.venueIdList'), (value, index) => {
    const venueId = id(value, `state.venueIdList[${index}]`);
    if (seen.has(venueId)) fail('state.venueIdList', `存在重复编号 ${venueId}`);
    seen.add(venueId);
    return venueId;
  });
  const venues = uniqueRows(own(state, 'venueList'), 'state.venueList', id).map(({ row, path, key: venueId }) => ({
    id: venueId, name: text(own(row, 'name'), `${path}.name`, true)
  })).filter(venue => seen.has(venue.id));
  const walletBalance = decimal(own(state, 'walletBalance'), 'state.walletBalance', { twoPlaces: true });
  const rewardAmount = decimal(own(state, 'rewardAmount'), 'state.rewardAmount', { twoPlaces: true });
  const venueId = state.selectedVenueId > 0 ? id(state.selectedVenueId, 'state.selectedVenueId') : '';
  if (venueId && !venues.some(venue => venue.id === venueId)) fail('state.selectedVenueId', '所选场馆已不可用');
  const eligible = boolean(own(state, 'eligible'), 'state.eligible') && (!venueRequired || Boolean(venueId));
  const claimed = boolean(own(state, 'claimed'), 'state.claimed');
  const claimRecordId = state.claimRecordId > 0 ? id(state.claimRecordId, 'state.claimRecordId') : '';
  if (venueRequired && claimed && (!venueId || !claimRecordId)) fail('state.claimRecordId', '原场馆领取凭证待核对');
  return {
    flow: 'settled', centerClaimSupported, requirement, venueRequired, claimRecordId, tiers, venues, venueIdList, tierId: '', venueId,
    limitLabel: ['同一账户在全部首存活动中共用一次机会', '同一场馆在全部首存活动中共用一次机会', '本活动限领取一次'][requirement],
    ...(state.applicationSupported === true ? {
      applicationSupported: true,
      rulesVersion: text(state.rulesVersion, 'state.rulesVersion', true),
      applicationMaximumCents: cents(decimal(state.applicationMaximum, 'state.applicationMaximum', { twoPlaces: true }), 'state.applicationMaximum'),
      walletAvailableCents: cents(decimal(state.walletAvailable, 'state.walletAvailable', { twoPlaces: true }), 'state.walletAvailable'),
      originalRequestId: typeof state.originalRequestId === 'string' ? state.originalRequestId : ''
    } : {}),
    actualDepositAmount: decimal(own(state, 'depositAmount'), 'state.depositAmount', { amount: true }),
    rewardAmount, eligible, claimed,
    reason: venueRequired && !venues.length ? '暂无可用的接入场馆，请稍后再试' : text(own(state, 'reason'), 'state.reason'), walletBalance,
    balanceCents: cents(walletBalance, 'state.walletBalance'),
    rewardCents: cents(rewardAmount, 'state.rewardAmount'),
    claimAvailable: eligible === true && claimed === false,
    selectedTierLabel: '按实际到账匹配',
    allowedVenueLabel: venues.map(venue => venue.name).join('、') || '—',
    estimatedTurnoverText: '以服务端核算为准',
    instructions: centerClaimSupported ? '核验首笔成功到账存款，彩金发至中心钱包，不转本金' : '首笔存款实际到账后领取'
  };
}

/**
 * Accepts unwrapped activity detail and state responses. Throws on wrong identity
 * or malformed facts. IDs are canonical strings; missing booleans/counts are null.
 * content is the unchanged detail HTML: the host must sanitize before rendering.
 * This projection does not authorize task claims, ballots or deposit parameters.
 */
export function adaptCampaignModel(activity, state, { dark = false } = {}) {
  object(activity, 'activity');
  object(state, 'state');
  const activityId = id(own(activity, 'id'), 'activity.id');
  if (id(own(state, 'activityId'), 'state.activityId') !== activityId) fail('state.activityId', '与活动详情不匹配');
  const activityType = own(activity, 'activityType');
  const kind = campaignKind(activityType);
  if (!kind) fail('activity.activityType', '不是支持的活动类型');
  // The real firstDeposit response has no activityType; reject it if supplied wrong.
  if ((kind !== 'deposit' || own(state, 'activityType') !== undefined) && own(state, 'activityType') !== activityType) {
    fail('state.activityType', '与活动详情不匹配');
  }
  const title = text(own(activity, 'title'), 'activity.title');
  const src = imageUrl(own(activity, 'mainImageUrl'), 'activity.mainImageUrl');
  // No verified theme-specific field exists in this API. Both themes use this URL.
  void dark;
  const model = {
    kind, activityId, activityType, title, hero: src ? { src, alt: title } : null,
    content: text(own(activity, 'content'), 'activity.content')
  };
  if (kind === 'deposit') return { ...model, ...deposit(state) };
  const rules = object(own(state, 'rules'), 'state.rules');
  const version = own(rules, 'version');
  if (version !== undefined && version !== 1) fail('state.rules.version', '玩法版本无效');
  const progress = progressMap(state);
  return { ...model, ...(kind === 'newcomer' ? newcomer(state, rules, progress) : vote(state, rules, progress)) };
}
